import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import type { CookieValueTypes } from 'cookies-next'
import type { OptionsType } from 'cookies-next/lib/types'
import type { NextApiRequest, NextApiResponse } from 'next'
import type { NextApiRequestCookies } from 'next/dist/server/api-utils'
import type { IncomingMessage, ServerResponse } from 'node:http'
import type { ParsedUrlQuery } from 'node:querystring'
import z from 'zod'

import { APP_ENV, DOMAIN } from '@/config/env'
import { reportErrorMessage } from '@/services/errorReporting'
import { COOKIES_CATEGORIES, cookieNames } from '@helpers/cookies/utils'

type CookieValues = {
  referrerName?: CookieValueTypes | string[]
  referralCode?: CookieValueTypes | string[]
  campaign?: CookieValueTypes | string[]
  coupon?: CookieValueTypes | string[]
  fa_tracking_parameter?: CookieValueTypes | string[]
  aw_tracking_parameter?: CookieValueTypes | string[]
  utm_source?: CookieValueTypes | string[]
  utm_medium?: CookieValueTypes | string[]
  utm_campaign?: CookieValueTypes | string[]
  fbclid?: CookieValueTypes | string[]
}

export const readUrlParams = (query: URLSearchParams): CookieValues => {
  const referralCode =
    query.get('utm_medium') === 'refer_a_friend' ? query.get('utm_source') : query.get('r')

  // elements are null if not found, therefore we use undefined instead to set cookies
  return {
    referrerName: query.get('n') ?? undefined,
    referralCode: referralCode ?? undefined,
    campaign: query.get('campaign') ?? undefined,
    coupon: query.get('coupon') ?? undefined,
    fa_tracking_parameter: query.get('s_id') ?? undefined,
    aw_tracking_parameter: query.get('awc') ?? undefined,
    utm_source: query.get('utm_source') ?? undefined,
    utm_medium: query.get('utm_medium') ?? undefined,
    utm_campaign: query.get('utm_campaign') ?? undefined,
    fbclid: query.get('fbclid') ?? undefined,
  }
}

const EXPIRATION_TIME_MS = 14 * 24 * 60 * 60 * 1000

const maybeSetCookie = (
  name: string,
  value: CookieValueTypes | string[],
  allowedCookies: string[],
  options?: OptionsType
) => {
  if (value !== undefined && value !== null && allowedCookies.indexOf(name) !== -1) {
    const preparedOptions: OptionsType = {
      ...options,
      maxAge: EXPIRATION_TIME_MS,
      path: '/',
    }

    // Don't apply the following options for localhost for cookies to work there.
    if (!DOMAIN.includes('localhost')) {
      preparedOptions.httpOnly = options?.httpOnly
      preparedOptions.secure = options?.secure

      // Setting the domain ("hellogetsafe.com", without the subdomain "www")
      // enables cookies to be read from other sub domains as well (like start.hellogetsafe.com)
      preparedOptions.domain = APP_ENV === 'production' ? 'hellogetsafe.com' : 'gtsf.insure'
    }

    setCookie(name, value, preparedOptions)
  }
}

const getAllowedCookie = (cookieName: string, allowedCookies: string[]) => {
  if (allowedCookies.indexOf(cookieName) !== -1) {
    return getCookie(cookieName)
  }
  return undefined
}

const readCookies = (allowedCookies: string[]): CookieValues => {
  return {
    referrerName: getAllowedCookie(cookieNames.referrerName, allowedCookies),
    referralCode: getAllowedCookie(cookieNames.referralCode, allowedCookies),
    campaign: getAllowedCookie(cookieNames.campaign, allowedCookies),
    coupon: getAllowedCookie(cookieNames.coupon, allowedCookies),
    fa_tracking_parameter: getAllowedCookie(cookieNames.fa_tracking_parameter, allowedCookies),

    // Note that this cookie is being set with the "secure" flag (as required by Awin). This means
    // that it won't work when the website is run without https
    aw_tracking_parameter: getAllowedCookie(cookieNames.aw_tracking_parameter, allowedCookies),
    utm_source: getAllowedCookie(cookieNames.utm_source, allowedCookies),
    utm_medium: getAllowedCookie(cookieNames.utm_medium, allowedCookies),
    utm_campaign: getAllowedCookie(cookieNames.utm_campaign, allowedCookies),
  }
}

const returnCookieValues = (parsedCookies: CookieValues, urlParams: CookieValues) => ({
  referrerName: parsedCookies.referrerName || urlParams.referrerName,
  referralCode: parsedCookies.referralCode || urlParams.referralCode,
  campaign: parsedCookies.campaign || urlParams.campaign,
  coupon: parsedCookies.coupon || urlParams.coupon,
  fa_tracking_parameter: parsedCookies.fa_tracking_parameter || urlParams.fa_tracking_parameter,
  aw_tracking_parameter: parsedCookies.aw_tracking_parameter || urlParams.aw_tracking_parameter,
  utm_source: parsedCookies.utm_source || urlParams.utm_source,
  utm_medium: parsedCookies.utm_medium || urlParams.utm_medium,
  utm_campaign: parsedCookies.utm_campaign || urlParams.utm_campaign,
  fbclid: parsedCookies.fbclid || urlParams.fbclid,
})

export const getFromCookiesOrUrlParams = (query: URLSearchParams) => {
  const urlParams = readUrlParams(query)
  const parsedCookies = readCookies(COOKIES_CATEGORIES.necessary_cookies)

  return returnCookieValues(parsedCookies, urlParams)
}

const hasUtmParameters = ({ utm_source, utm_medium, utm_campaign }: CookieValues) => {
  return Boolean(utm_source || utm_medium || utm_campaign)
}

const removeUtmParametersFromCookies = () => {
  deleteCookie(cookieNames.utm_campaign)
  deleteCookie(cookieNames.utm_source)
  deleteCookie(cookieNames.utm_medium)
}

const saveUrlParamToCookies = (urlParams: CookieValues, allowedCookies: string[]) => {
  if (urlParams.coupon || urlParams.campaign) {
    deleteCookie(cookieNames.referralCode)
    deleteCookie(cookieNames.referrerName)
  }

  const referralCode = urlParams.referralCode
  if (referralCode) {
    deleteCookie(cookieNames.campaign)
    deleteCookie(cookieNames.coupon)

    const sanitizedCode = String(referralCode).slice(0, 9)
    if (referralCode === sanitizedCode) {
      maybeSetCookie(cookieNames.referralCode, sanitizedCode, allowedCookies)
    } else {
      reportErrorMessage(`There are invalid characters in the referral code: ${referralCode}`)
    }
  }

  maybeSetCookie(cookieNames.referrerName, urlParams.referrerName, allowedCookies)
  maybeSetCookie(cookieNames.referralCode, urlParams.referralCode, allowedCookies)
  maybeSetCookie(cookieNames.campaign, urlParams.campaign, allowedCookies)
  maybeSetCookie(cookieNames.coupon, urlParams.coupon, allowedCookies)

  maybeSetCookie(cookieNames.fa_tracking_parameter, urlParams.fa_tracking_parameter, allowedCookies)
  maybeSetCookie(cookieNames.aw_tracking_parameter, urlParams.aw_tracking_parameter, allowedCookies)
}

export const queryToSearchParams = (query: ParsedUrlQuery) => {
  const searchParams = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, Array.isArray(value) ? value.join(',') : value)
    }
  })
  return searchParams
}

export const getFacebookParams = (serverParams?: {
  req: IncomingMessage & { cookies: NextApiRequestCookies }
  res: ServerResponse<IncomingMessage>
}): undefined | { fbc: CookieValueTypes; fbp: CookieValueTypes } => {
  const fbc = getCookie('_fbc', serverParams)

  const fbp = getCookie('_fbp', serverParams)

  if (fbc && fbp) {
    return { fbc, fbp }
  }
}

export const setFacebookCookies = (
  cookieValues: CookieValues,
  serverParams?: {
    req: IncomingMessage & { cookies: NextApiRequestCookies }
    res: ServerResponse<IncomingMessage>
  }
) => {
  const fbclid = cookieValues.fbclid

  if (!fbclid) {
    // removing existing cookies if present
    deleteCookie('_fbc', serverParams)
    deleteCookie('_fbp', serverParams)

    return
  }

  const allowed_keys = ['_fbc', '_fbp']

  const timestamp = Date.now()

  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#2--format-clickid
  const fbc = `fb.1.${timestamp}.${fbclid}`

  const uniuqeId = getCookie(cookieNames.browser_id, serverParams)

  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#fbp
  const fbp = `fb.1.${timestamp}.${uniuqeId}`

  maybeSetCookie('_fbc', fbc, allowed_keys, serverParams)
  maybeSetCookie('_fbp', fbp, allowed_keys, serverParams)

  return
}

/*
This function
1. reads parameters from the URL
2. reads parameters from cookies
3. saves parameters from URL to cookies
4. returns values set either in the URL or in cookies
 */
export const processCookies = (query: ParsedUrlQuery, allowedCookies: string[]): CookieValues => {
  const urlParams = readUrlParams(queryToSearchParams(query))
  if (!hasUtmParameters(urlParams)) {
    removeUtmParametersFromCookies()
  }
  saveUrlParamToCookies(urlParams, allowedCookies)
  setFacebookCookies(urlParams)

  try {
    const parsedCookies = readCookies(allowedCookies)
    // It looks like we could just return the `cookies` variable, since it looks like it contains both
    // URL parameters and cookies at this point (setting cookies is a sync operation). But on the
    // server setting a cookie is done on the response object, while reading a cookie is done on the
    // request. Therefore it is not possible to read a cookie that was just set.

    return returnCookieValues(parsedCookies, urlParams)
  } catch {
    return urlParams
  }
}

export const checkCookieFirstConsent = (params: {
  keys: string[]
  req: NextApiRequest
  res: NextApiResponse
}) => {
  const { keys, req, res } = params

  const cookieFirst = getCookie('cookiefirst-consent', { req, res })

  const schema = z.string().transform((value) => {
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  })

  const parsedCookie = schema.safeParse(cookieFirst)

  return parsedCookie.success ? keys.every((key) => parsedCookie.data[key] === true) : false
}
